import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useDwollaStore, useAuthStore } from '../../stores/root_store';

export const SettingsPayouts = observer(() => {
  const dwollaStore = useDwollaStore();
  const authStore = useAuthStore();
  const [customerForm, setCustomerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  const [bankForm, setBankForm] = useState({
    routing_number: '',
    account_number: '',
    account_type: 'checking' as 'checking' | 'savings',
    name: '',
  });
  const [showBankForm, setShowBankForm] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [sourceToRemove, setSourceToRemove] = useState<string | null>(null);

  // Initialize form with user data
  useEffect(() => {
    if (authStore.user) {
      setCustomerForm({
        first_name: authStore.user.first_name || '',
        last_name: authStore.user.last_name || '',
        email: authStore.user.primary_email || '',
      });
    }
  }, [authStore.user]);

  // Fetch status on mount
  useEffect(() => {
    dwollaStore.fetchStatus();
  }, [dwollaStore]);

  // Fetch sources and transactions when customer is set up
  useEffect(() => {
    if (dwollaStore.status?.has_customer) {
      dwollaStore.fetchSources();
      dwollaStore.fetchTransactions();
    }
  }, [dwollaStore, dwollaStore.status?.has_customer]);

  const handleCreateCustomer = async () => {
    const success = await dwollaStore.createCustomer(
      customerForm.first_name,
      customerForm.last_name,
      customerForm.email
    );
    if (success) {
      setShowBankForm(true);
    }
  };

  const handleAddBankAccount = async () => {
    const success = await dwollaStore.addFundingSource(
      bankForm.routing_number,
      bankForm.account_number,
      bankForm.account_type,
      bankForm.name || undefined
    );
    if (success) {
      setBankForm({
        routing_number: '',
        account_number: '',
        account_type: 'checking',
        name: '',
      });
      setShowBankForm(false);
      dwollaStore.fetchStatus();
    }
  };

  const handleRemoveSource = async () => {
    if (sourceToRemove) {
      await dwollaStore.removeSource(sourceToRemove);
    }
    setRemoveDialogOpen(false);
    setSourceToRemove(null);
  };

  const openRemoveDialog = (sourceUrl: string) => {
    setSourceToRemove(sourceUrl);
    setRemoveDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(parseFloat(amount));
  };

  if (dwollaStore.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Step 1: Create Dwolla customer
  if (!dwollaStore.status?.has_customer) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Set Up Payouts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          To receive payments, we need to set up your payout account. This is a one-time setup.
        </Typography>

        {dwollaStore.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {dwollaStore.error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="First Name"
              value={customerForm.first_name}
              onChange={(e) => setCustomerForm({ ...customerForm, first_name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Last Name"
              value={customerForm.last_name}
              onChange={(e) => setCustomerForm({ ...customerForm, last_name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={customerForm.email}
              onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
              required
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleCreateCustomer}
              disabled={
                dwollaStore.saving ||
                !customerForm.first_name ||
                !customerForm.last_name ||
                !customerForm.email
              }
            >
              {dwollaStore.saving ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Step 2: Add bank account (show form if no funding source or user clicked "Add")
  if (!dwollaStore.status?.has_funding_source || showBankForm) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Add Bank Account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your bank account information to receive payments.
        </Typography>

        {dwollaStore.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {dwollaStore.error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Account Nickname (optional)"
              value={bankForm.name}
              onChange={(e) => setBankForm({ ...bankForm, name: e.target.value })}
              placeholder="e.g., My Checking"
              fullWidth
            />
            <TextField
              label="Routing Number"
              value={bankForm.routing_number}
              onChange={(e) => setBankForm({ ...bankForm, routing_number: e.target.value.replace(/\D/g, '') })}
              required
              fullWidth
              inputProps={{ maxLength: 9 }}
              helperText="9-digit routing number"
            />
            <TextField
              label="Account Number"
              value={bankForm.account_number}
              onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value.replace(/\D/g, '') })}
              required
              fullWidth
              inputProps={{ maxLength: 17 }}
            />
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={bankForm.account_type}
                label="Account Type"
                onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value as 'checking' | 'savings' })}
              >
                <MenuItem value="checking">Checking</MenuItem>
                <MenuItem value="savings">Savings</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {showBankForm && dwollaStore.status?.has_funding_source && (
                <Button
                  variant="outlined"
                  onClick={() => setShowBankForm(false)}
                  disabled={dwollaStore.saving}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleAddBankAccount}
                disabled={
                  dwollaStore.saving ||
                  !bankForm.routing_number ||
                  !bankForm.account_number ||
                  bankForm.routing_number.length !== 9
                }
                fullWidth={!showBankForm || !dwollaStore.status?.has_funding_source}
              >
                {dwollaStore.saving ? <CircularProgress size={24} /> : 'Add Bank Account'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Step 3: Fully set up - show bank accounts and transactions
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Payout Settings
        </Typography>
        <Link component={RouterLink} to="/assistant/payments">
          Payment Breakdowns
        </Link>
      </Box>

      {dwollaStore.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dwollaStore.clearError()}>
          {dwollaStore.error}
        </Alert>
      )}

      {/* Bank Accounts */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon />
          Bank Accounts
        </Typography>

        {dwollaStore.loadingSources ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : dwollaStore.sources.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No bank accounts connected.
          </Typography>
        ) : (
          <List dense>
            {dwollaStore.sources.map((source) => (
              <ListItem key={source.id}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {source.name}
                      {source.bank_name && (
                        <Typography variant="body2" color="text.secondary">
                          ({source.bank_name})
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={`${source.bank_account_type || 'Account'} - Added ${formatDate(source.created)}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => openRemoveDialog(source._links?.self?.href)}
                    disabled={dwollaStore.saving}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 1 }}
          onClick={() => setShowBankForm(true)}
        >
          Add Another Bank Account
        </Button>
      </Paper>

      {/* Transaction History */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Payout History
        </Typography>

        {dwollaStore.loadingTransactions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : dwollaStore.transactions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No payment history yet.
          </Typography>
        ) : (
          <List dense>
            {dwollaStore.transactions.map((transaction, index) => (
              <Box key={transaction.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={formatCurrency(transaction.amount, transaction.currency)}
                    secondary={formatDate(transaction.created)}
                  />
                  <Chip
                    label={transaction.status}
                    size="small"
                    color={
                      transaction.status === 'processed'
                        ? 'success'
                        : transaction.status === 'pending'
                          ? 'warning'
                          : transaction.status === 'failed'
                            ? 'error'
                            : 'default'
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {/* Remove Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Bank Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this bank account? You can add a new one later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveSource} color="error" disabled={dwollaStore.saving}>
            {dwollaStore.saving ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
